import type {DeepPartial} from '@bitstillery/mithril'
import type {GetPortalProfileResponse} from '@bitstillery/common/api/fact2server'
import type {Filter} from '@bitstillery/common/types'
import type {SupportContact} from '@bitstillery/common/lib/support'

/**
 * A collection's filter selections and sort, as `lookup_filters_sort` stores them in `$s.lookup`. A
 * legacy entry stored the whole filter instead, so it can lack `collapsed` and hold a type and serialize.
 */
export interface CollectionLookup {
    filters: Record<
        string,
        {collapsed?: boolean; selection?: unknown; serialize?: Filter['serialize']; type?: Filter['type'] | null}
    >
    modified: number
    sort: {by: string; order: 'asc' | 'desc'}
}

export interface StateCommon {
    context: {
        count?: number | null
        /** The context which is edited; most of the times a Collection row */
        data: object
        /** When opening a page; use this context by default */
        default: boolean
        icon?: string
        id: null | number | string
        loading: boolean
        key?: string
        mode: 'inline' | 'panel'
        /** This field is used also to determine the title from i18n */
        name: null | string
        /** The title of the context panel */
        title?: string
    }
    env: {
        browserName: string
        ctrl_key: boolean
        isBrowser: boolean
        isChrome: boolean
        isFirefox: boolean
        isSafari: boolean
        // 'unknown' when no breakpoint query matches.
        layout: 'mobile' | 'tablet' | 'desktop' | 'unknown'
        key: number
        qs: Record<string, string>
        ua: string
        uri: string
    }
    identity: {
        /** The session JWT's `user_artkey` and `exp` (a Unix timestamp), which only Discover's login decodes. */
        artkey: number | null
        exp: number | null
        token: string | null
        /** Discover's user or the portal's profile; only the portal's carries the profile fields `set_language` posts. */
        user: {
            /** Only the portal's profile has one; Discover's user doesn't. */
            language?: string
            decimal_locale?: string
            is_superuser?: boolean
            is_sales_target_manager?: boolean
            /** Null while the portal's profile is still its placeholder. */
            artkey?: number | null
            name?: string
        } & Partial<
            Pick<
                GetPortalProfileResponse,
                | 'email_address'
                | 'first_name'
                | 'last_name'
                | 'mobile_phone_number'
                | 'phone_number'
                | 'price_preference'
                | 'timezone'
            >
        >
    }
    // A toggle that changes the listed price to include excise tax
    include_excise: boolean
    language: string
    /** Light/dark preference: 'light' | 'dark' | 'auto' (follow OS). Cookie-tier in portal (SSR), saved in discover. */
    theme: string
    // A generic value cache for ui-elements that need to persist a value that survives a page reload;
    // each key holds whatever its owner stores there (a collection's filters and sort, a picked artkey).
    lookup: Record<string, CollectionLookup | number | string>
    margin: {
        approval: number
        low: number
        target: number
    }
    notifications: []
    panels: {
        context: {
            active: boolean
            collapsed: boolean
        }
        left: {
            collapsed: boolean
        }
        view: {
            categories: {collapsed: boolean}
            filters: {collapsed: boolean}
            menu: {collapsed: boolean}
        }
    }
    scheme: {
        changed: boolean
        version: string
    }
    /** Absent until `store.load()`, which module-level code can run ahead of. */
    tab?: {
        /** Only Discover's tab tier sets it. */
        account?: {
            slug: string
        }
    }
    settings: {
        beta: boolean
    }
    support: {
        collapsed: boolean
        manager: {
            email_address: string
            mobile_telephone_number: string
            name: string
            phone_number: string
        }
        /** Computed property that returns the current support contact information */
        contact?: SupportContact
    }
    currencies?: {
        default: string
        /** Discover keeps the API's decimal strings; readers convert with `Number()`. */
        exchange_rates: Record<string, {rate: number | string}>
    }
    issues?: {
        collapsed?: boolean
    }
}

export const saved = {
    context: {
        default: true,
    },
    identity: {
        artkey: null,
        exp: null,
        token: null,
    },
    issues: {
        collapsed: true,
    },
    // Unset on purpose: `init_language` resolves it (URL, profile, domain, browser), and a default
    // here would win over the domain check as if the visitor had chosen it.
    language: '',
    lookup: {},
    // Light unless the user picks otherwise; the theme switcher that offers 'auto'/'dark' is beta-gated.
    theme: 'light',
    panels: {
        context: {
            active: false,
            /** Cart panel collapse: persisted; default collapsed; only shop UI toggles should change this. */
            collapsed: true,
        },
        left: {
            collapsed: false,
        },
        view: {
            categories: {
                collapsed: true,
            },
            filters: {
                collapsed: true,
            },
            menu: {
                collapsed: true,
            },
        },
    },
    scheme: {
        changed: false,
        version: process.env.MSI_PACKAGE === 'discover' ? process.env.MSI_SCHEME_DISCOVER : process.env.MSI_SCHEME_PORTAL,
    },
    settings: {
        beta: false,
    },
    support: {
        collapsed: false,
        manager: {
            email_address: '',
            mobile_telephone_number: '',
            name: '',
            phone_number: '',
        },
    },
} satisfies DeepPartial<StateCommon>

export const tab = {}

export const session = {}

export const temporary = {
    context: {
        count: null,
        data: {},
        icon: '',
        id: null,
        loading: false,
        name: null,
    },
    env: {
        browserName: '',
        ctrl_key: false,
        isBrowser: true,
        isChrome: false,
        isFirefox: false,
        isSafari: false,
        /** Stable for SSR hydration; Layout uses `key={$s.env.key}` (Date.now() differed server vs client). */
        key: 0,
        layout: 'desktop',
        qs: {},
        ua: 'unknown',
        uri: '',
    },
    identity: {
        user: {},
    },
    include_excise: false,
    margin: {
        approval: 0,
        low: 0,
        target: 0,
    },
    notifications: [],
} satisfies DeepPartial<StateCommon>

/** Volatile state: in-memory only, lost on reload. Alias for temporary. */
export const volatile = temporary

/** Persistent state: survives page reload (localStorage). Alias for saved. */
export const persistent = saved
